import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { PageShell, Text } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import { fetchPlayerOutline } from "@/services/api/player";

export function TenantLearnIndexPage() {
  const { tenantSlug = "", courseSlug = "" } = useParams<{
    tenantSlug: string;
    courseSlug: string;
  }>();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const t = useTranslations("player");

  const outlineQuery = useQuery({
    queryKey: ["player-outline", courseSlug],
    queryFn: () => fetchPlayerOutline(courseSlug),
    enabled: isAuthenticated && Boolean(courseSlug),
    retry: false,
    // LRN-10: the resume target must be current — never redirect from an outline cached earlier
    // in the visit (the lesson page shares this query).
    refetchOnMount: "always",
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (outlineQuery.isLoading || outlineQuery.isFetching) {
    return (
      <PageShell data-testid="course-player-loading">
        <Text tone="muted">{t("loading")}</Text>
      </PageShell>
    );
  }

  if (outlineQuery.isError) {
    const status = (outlineQuery.error as { response?: { status?: number } }).response?.status;
    if (status === 403) {
      return <Navigate to={`/t/${tenantSlug}/courses/${courseSlug}`} replace />;
    }
    return (
      <PageShell data-testid="course-player-not-found">
        <Text tone="muted">{status === 404 ? t("courseNotFound") : t("courseLoadFailed")}</Text>
      </PageShell>
    );
  }

  // LRN-10: "Continue learning" lands where the student left off (server-computed, skips
  // locked lessons); with nothing open yet, the first lesson shows its locked notice.
  const targetLessonId =
    outlineQuery.data?.resume?.lessonId ??
    outlineQuery.data?.modules.flatMap((mod) => mod.lessons)[0]?.id;
  if (!targetLessonId) {
    return (
      <PageShell data-testid="course-player-empty">
        <Text tone="muted">{t("noLessons")}</Text>
      </PageShell>
    );
  }

  return <Navigate to={`/t/${tenantSlug}/learn/${courseSlug}/lessons/${targetLessonId}`} replace />;
}
